import { useEffect, useState } from "react";
import type { Address } from "viem";
import { isAddress } from "viem";
import { config } from "../config";
import { stakingVaultAbi } from "../abi";
import { l2Public } from "../lib/chain";
import { approveIfNeeded, writeL2 } from "../lib/tx";
import { fmtToken, parseToken } from "../lib/format";
import { useWallet } from "../wallet";
import { ActionButton, Card, Field, Notice, Stat } from "../components/ui";
import { ConnectPrompt } from "./Account";

export function Stake() {
  const { account } = useWallet();
  // A (brain, validator) pair handed over from the explorer / positions table, if any.
  const [brainId, setBrainId] = useState(() => {
    try {
      const raw = sessionStorage.getItem("basedai.stake.prefill");
      return raw ? (JSON.parse(raw).brainId as number) ?? 7 : 7;
    } catch {
      return 7;
    }
  });
  const [validator, setValidator] = useState(() => {
    try {
      const raw = sessionStorage.getItem("basedai.stake.prefill");
      sessionStorage.removeItem("basedai.stake.prefill");
      return raw ? (JSON.parse(raw).validator as string) ?? "" : "";
    } catch {
      return "";
    }
  });
  const [amount, setAmount] = useState("10");
  const [active, setActive] = useState<bigint | null>(null);
  const [pending, setPending] = useState<bigint | null>(null);
  const [validatorTotal, setValidatorTotal] = useState<bigint | null>(null);
  const [nonce, setNonce] = useState(0);

  const validAddr = isAddress(validator);

  useEffect(() => {
    if (!account || !validAddr) {
      setActive(null);
      setPending(null);
      setValidatorTotal(null);
      return;
    }
    const c = config.l2.contracts;
    const v = validator as Address;
    l2Public.readContract({ address: c.stakingVault, abi: stakingVaultAbi, functionName: "stakerBalance", args: [BigInt(brainId), v, account] }).then((b) => setActive(b as bigint)).catch(() => setActive(null));
    l2Public.readContract({ address: c.stakingVault, abi: stakingVaultAbi, functionName: "pendingBalance", args: [BigInt(brainId), v, account] }).then((b) => setPending(b as bigint)).catch(() => setPending(null));
    l2Public.readContract({ address: c.stakingVault, abi: stakingVaultAbi, functionName: "validatorStake", args: [BigInt(brainId), v] }).then((b) => setValidatorTotal(b as bigint)).catch(() => setValidatorTotal(null));
  }, [account, brainId, validator, validAddr, nonce]);

  if (!account) return <ConnectPrompt />;

  const vault = config.l2.contracts.stakingVault;
  const v = validator as Address;
  const refresh = () => setNonce((n) => n + 1);

  return (
    <>
      <div className="section-title">Stake &amp; earn yield</div>
      <Notice>
        Stake $basedAI to a <strong>(Brain, validator)</strong> pool. Your share of that validator&apos;s fee income
        accrues as yield; unstaking has a 14-day cooldown and stays slashable during it.
      </Notice>

      <div className="grid cols-2" style={{ marginTop: 18 }}>
        <Card title="Position">
          <Field label="Brain ID">
            <input className="input" type="number" min={0} max={63} value={brainId} onChange={(e) => setBrainId(Math.max(0, Math.min(63, Number(e.target.value))))} style={{ maxWidth: 160 }} />
          </Field>
          <Field label="Validator address" hint={validator && !validAddr ? "Not a valid address." : "The validator you delegate to."}>
            <input className="input mono" value={validator} onChange={(e) => setValidator(e.target.value)} placeholder="0x…" />
          </Field>
          <div className="grid cols-2">
            <Stat label="Your active stake" value={active !== null ? fmtToken(active) : "—"} unit="BASED" />
            <Stat label="In cooldown" value={pending !== null ? fmtToken(pending) : "—"} unit="BASED" />
          </div>
          <Stat label="Validator total stake" value={validatorTotal !== null ? fmtToken(validatorTotal) : "—"} unit="BASED" />
        </Card>

        <div className="stack">
          <Card title="Stake">
            <Field label="Amount ($basedAI)">
              <input className="input" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </Field>
            <ActionButton
              disabled={!validAddr}
              onRun={async () => {
                const amt = parseToken(amount);
                await approveIfNeeded(account, config.l2.contracts.based, vault, amt);
                return writeL2(account, { address: vault, abi: stakingVaultAbi, functionName: "stake", args: [BigInt(brainId), v, amt] });
              }}
              onDone={refresh}
            >
              Approve &amp; stake
            </ActionButton>
          </Card>

          <Card title="Unstake" sub="Request, wait 14 days, then claim.">
            <Field label="Amount ($basedAI)">
              <input className="input" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </Field>
            <div className="row">
              <ActionButton variant="ghost" disabled={!validAddr} onRun={() => writeL2(account, { address: vault, abi: stakingVaultAbi, functionName: "requestUnstake", args: [BigInt(brainId), v, parseToken(amount)] })} onDone={refresh}>
                Request unstake
              </ActionButton>
              <ActionButton disabled={!validAddr} onRun={() => writeL2(account, { address: vault, abi: stakingVaultAbi, functionName: "claimUnstake", args: [BigInt(brainId), v] })} onDone={refresh}>
                Claim
              </ActionButton>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}
